import {
	Alert,
	Avatar,
	Button,
	Divider,
	Grid,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { apiHooks } from "../../lib/apis/norsk-gassnett-api-hooks";
import LoadingButton from "../../lib/components/atomics/loading-button";
import MUIIcon from "../../lib/components/atomics/mui-icon";
import SubpageWrapper from "../../lib/components/atomics/subpage-wrapper";
import TranslatedLink from "../../lib/components/complex/translated-link";
import {
	FormCheckboxField,
	FormDatePickerField,
	FormNumberField,
	FormTelephoneField,
	FormTextField,
} from "../../lib/components/ui/forms";
import { getBorderRadius } from "../../lib/helpers/utils";

const RelocateFormSchema = z.object({
	meterReading: z
		.string()
		.min(1)
		.refine((arg) => z.coerce.number().min(0).safeParse(arg).success),
	relocationDate: z.string().min(1),
	invoiceAddress: z.string().min(1),
	postalCode: z.string().min(1),
	place: z.string().min(1),
	newOwnerName: z.string().min(1),
	newOwnerBirthdate: z.string().min(1),
	newOwnerTlf: z.string().min(1),
	newOwnerEmail: z.email(),
	newOwnerAddress: z.string(),
	newOwnerPostalCode: z.string(),
	newOwnerPlace: z.string(),
	newOwnerIsAware: z.literal(true),
});

export default function Relocate() {
	const { t } = useTranslation();

	const relocateCustomer = apiHooks.useMutation(
		"post",
		"/v1/customer/relocate",
	);
	const form = useForm({
		defaultValues: {
			meterReading: "",
			relocationDate: "",
			invoiceAddress: "",
			postalCode: "",
			place: "",
			newOwnerName: "",
			newOwnerBirthdate: "",
			newOwnerTlf: "",
			newOwnerEmail: "",
			newOwnerAddress: "",
			newOwnerPostalCode: "",
			newOwnerPlace: "",
			newOwnerIsAware: false,
		},
		onSubmit: async ({ value }) => {
			await relocateCustomer.mutateAsync({
				body: {
					...value,
					meterReading: parseInt(value.meterReading, 10),
				},
			});
		},
	});

	return (
		<SubpageWrapper
			title={t("relocate.title")}
			backRef="/"
			specialAction={
				!relocateCustomer.isSuccess && (
					<ClearFormButton
						onClick={() => form.reset()}
						disabled={relocateCustomer.isPending}
					/>
				)
			}
		>
			{relocateCustomer.isSuccess ? (
				<Paper sx={{ width: 1, p: 3, borderRadius: getBorderRadius }}>
					<Alert>
						<TranslatedLink
							linkProps={{ underline: "always" }}
							to="/"
							content="relocate.success"
						/>
					</Alert>
				</Paper>
			) : (
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<Stack gap={3}>
						<FormSection
							index={1}
							title={t("relocate.your_info")}
							info={t("relocate.meter_reading_info")}
							required
						>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="meterReading"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormNumberField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="relocationDate"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormDatePickerField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<form.Field
									name="invoiceAddress"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="postalCode"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="place"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} required />;
									}}
								</form.Field>
							</Grid>
						</FormSection>

						<FormSection
							index={2}
							title={t("relocate.new_owner_info")}
							info={t("relocate.missing_owner_info")}
						>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="newOwnerName"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="newOwnerBirthdate"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormDatePickerField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 5 }}>
								<form.Field
									name="newOwnerTlf"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTelephoneField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 7 }}>
								<form.Field
									name="newOwnerEmail"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} required />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<form.Field
									name="newOwnerAddress"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="newOwnerPostalCode"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ md: 6 }}>
								<form.Field
									name="newOwnerPlace"
									validators={{
										onChange: ({ fieldApi, value }) =>
											!RelocateFormSchema.shape[fieldApi.name].safeParse(value)
												.success
												? t(`relocate.${fieldApi.name}Error`)
												: undefined,
									}}
								>
									{(field) => {
										return <FormTextField field={field} />;
									}}
								</form.Field>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<form.Field
									name="newOwnerIsAware"
									validators={{
										onSubmit: ({ fieldApi, value }) =>
											value ? undefined : t(`relocate.${fieldApi.name}Error`),
									}}
								>
									{(field) => {
										return <FormCheckboxField field={field} required />;
									}}
								</form.Field>
							</Grid>

							<form.Subscribe
								selector={(state) => [
									state.canSubmit,
									state.isSubmitting,
									state.values,
								]}
							>
								{([canSubmit, isSubmitting, values]) => {
									const isFormValid =
										RelocateFormSchema.safeParse(values).success;
									return (
										<Stack
											p={2}
											direction="row"
											justifyContent="space-between"
											alignItems="center"
										>
											<ClearFormButton
												onClick={() => form.reset()}
												disabled={isSubmitting}
											/>

											<LoadingButton
												type="submit"
												endIcon={<MUIIcon name="arrow_forward" />}
												disabled={!canSubmit || !isFormValid}
												loading={isSubmitting}
											>
												{t("relocate.submit_form")}
											</LoadingButton>
										</Stack>
									);
								}}
							</form.Subscribe>
						</FormSection>
					</Stack>
				</form>
			)}
		</SubpageWrapper>
	);
}

interface ClearFormButtonProps {
	onClick: () => void;
	disabled?: boolean;
}

const ClearFormButton: React.FC<ClearFormButtonProps> = ({
	onClick,
	disabled,
}) => {
	const { t } = useTranslation();
	return (
		<Button
			onClick={onClick}
			variant="text"
			disabled={disabled}
			startIcon={<MUIIcon name="delete_forever" color="error" filled />}
		>
			{t("relocate.empty_form")}
		</Button>
	);
};

interface FormSectionProps {
	index: number;
	title: string;
	required?: boolean;
	info?: string;
	children: React.ReactNode;
}

const FormSection: React.FC<FormSectionProps> = ({
	index,
	title,
	required,
	info,
	children,
}) => {
	const { t } = useTranslation();
	const color = index === 1 ? "primary" : "secondary";
	const borderPadding = "40px";
	return (
		<Paper sx={{ width: 1, p: 0, borderRadius: getBorderRadius }}>
			<Stack
				sx={{ width: 1, p: borderPadding, pb: 0 }}
				direction={{ xs: "column", md: "row" }}
				alignItems="center"
				justifyContent="space-between"
			>
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					sx={{ placeSelf: "start" }}
				>
					<Avatar
						sx={{
							color: `${color}.contrastText`,
							backgroundColor: `${color}.main`,
						}}
					>
						{index}
					</Avatar>
					<Typography variant="h3">{title}</Typography>
				</Stack>
				{required && (
					<Typography sx={{ fontStyle: "italic" }}>
						{t("relocate.required_field")}
					</Typography>
				)}
			</Stack>
			<Typography
				textAlign={{ xs: "center", md: "left" }}
				variant="body1"
				mx={borderPadding}
				mt={2}
				mb={3}
			>
				{info}
			</Typography>
			<Divider />
			<Grid sx={{ p: borderPadding }} container spacing={3}>
				{children}
			</Grid>
		</Paper>
	);
};
